/**
 * Configuração por coluna (grid) — alinhamento, responsivo, fundo, ondas, CSS.
 * @package gepetto-frontend
 */

import { migrateColumnConfigResponsive, columnResponsiveClassNames as resolveColumnResponsiveClasses } from './responsiveLayout';

export const DEFAULT_COLUMN_CONFIG = {
  textAlign: '',
  columnAlign: '',
  centerOnSmall: false,
  orderSmall: 0,
  heightPx: 0,
  paddingTop: 0,
  paddingBottom: 0,
  /** Espaço à direita desta coluna (px) — entre esta coluna e a próxima. */
  columnGapAfterPx: 0,
  bgImage: '',
  backgroundColor: '',
  customClass: '',
  waveConfig: { enabled: false },
  advancedCss: '',
};

export const COLUMN_GRID_BASE_PADDING_PX = 15;

export function normalizeColumnConfig(raw) {
  const c = raw && typeof raw === 'object' ? raw : {};
  const textAlign = ['left', 'center', 'right'].includes(c.textAlign) ? c.textAlign : '';
  const columnAlign = ['left', 'center', 'right'].includes(c.columnAlign) ? c.columnAlign : '';
  const base = {
    ...DEFAULT_COLUMN_CONFIG,
    textAlign,
    columnAlign,
    centerOnSmall: !!(c.responsive?.mobile?.centerOnSmall ?? c.centerOnSmall),
    orderSmall: Math.max(0, Math.min(12, parseInt(c.responsive?.mobile?.orderSmall ?? c.orderSmall, 10) || 0)),
    heightPx: Math.max(0, Math.min(800, parseInt(c.heightPx, 10) || 0)),
    paddingTop: Math.max(0, Math.min(200, parseInt(c.paddingTop, 10) || 0)),
    paddingBottom: Math.max(0, Math.min(200, parseInt(c.paddingBottom, 10) || 0)),
    columnGapAfterPx: Math.max(0, Math.min(32, parseInt(c.columnGapAfterPx, 10) || 0)),
    bgImage: String(c.bgImage || '').trim(),
    backgroundColor: (c.backgroundColor != null && c.backgroundColor !== '') ? String(c.backgroundColor).trim() : '',
    customClass: String(c.customClass || '').trim(),
    waveConfig: c.waveConfig && typeof c.waveConfig === 'object' ? { ...DEFAULT_COLUMN_CONFIG.waveConfig, ...c.waveConfig } : { enabled: false },
    advancedCss: String(c.advancedCss || ''),
    responsive: c.responsive,
  };
  return migrateColumnConfigResponsive(base);
}

export function columnAlignToFlex(align) {
  if (align === 'left') return 'flex-start';
  if (align === 'center') return 'center';
  if (align === 'right') return 'flex-end';
  return '';
}

/** Classes utilitárias definidas no CSS de exportação do layout. */
export function columnResponsiveClassNames(cc) {
  return resolveColumnResponsiveClasses(cc);
}

export function sanitizeColumnCustomClass(cls) {
  return String(cls || '').replace(/[^a-zA-Z0-9_\- ]/g, '').trim();
}

export function clampColumnGapAfterPx(raw) {
  const n = typeof raw === 'number' ? raw : parseInt(String(raw ?? ''), 10);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(32, Math.round(n));
}

/** Fração da linha (0–1) ocupada por esta coluna. */
export function columnWidthFraction(col) {
  if (!col) return 1;
  if (col.width === 'dynamic') return (Number(col.customWidth) || 33.33) / 100;
  const map = { '1/1': 1, '1/2': 0.5, '1/3': 1 / 3, '2/3': 2 / 3, '1/4': 0.25 };
  return map[col.width] ?? 1;
}

/**
 * Metadados de espaçamento horizontal entre colunas de uma linha.
 * @returns {{ totalBetweenGapPx: number, gaps: number[], useColumnGap: boolean, columnGapPx: number }}
 */
export function getRowColumnGapMeta(columns) {
  const n = Array.isArray(columns) ? columns.length : 0;
  if (n < 2) {
    return { totalBetweenGapPx: 0, gaps: [], useColumnGap: false, columnGapPx: 0 };
  }
  const gaps = [];
  for (let i = 0; i < n - 1; i++) {
    gaps.push(normalizeColumnConfig(columns[i]?.config || {}).columnGapAfterPx);
  }
  const totalBetweenGapPx = gaps.reduce((sum, g) => sum + g, 0);
  const allSame = gaps.length > 0 && gaps.every((g) => g === gaps[0]);
  const useColumnGap = allSame && gaps[0] > 0 && gaps.every((g) => g > 0);
  return {
    totalBetweenGapPx,
    gaps,
    useColumnGap,
    columnGapPx: useColumnGap ? gaps[0] : 0,
  };
}

/** @deprecated Use getRowColumnGapMeta().totalBetweenGapPx */
export function rowColumnsBetweenGapPx(columns) {
  return getRowColumnGapMeta(columns).totalBetweenGapPx;
}

/** @deprecated alias */
export function rowColumnsTotalGapMarginPx(columns) {
  return rowColumnsBetweenGapPx(columns);
}

/** Largura flex de uma coluna quando há espaçamento entre colunas. */
export function columnFlexBasisWithGaps(col, totalBetweenGapPx) {
  const frac = columnWidthFraction(col);
  if (totalBetweenGapPx <= 0) return null;
  return `calc((100% - ${totalBetweenGapPx}px) * ${Number(frac.toFixed(6))})`;
}

/** Estilo do container flex da linha (palco + export). */
export function buildRowContainerGapStyle(columns) {
  const meta = getRowColumnGapMeta(columns);
  if (meta.totalBetweenGapPx <= 0) return null;
  const style = { flexWrap: 'nowrap' };
  if (meta.useColumnGap) {
    style.columnGap = `${meta.columnGapPx}px`;
  }
  return style;
}

/**
 * Estilos inline da coluna no palco (builder).
 * @returns {Record<string, string|number>|null}
 */
export function buildColumnGapLayoutStyle(col, colIdx, columns, { isNested = false } = {}) {
  if (!col || !Array.isArray(columns) || columns.length === 0) return null;

  const meta = getRowColumnGapMeta(columns);
  const frac = columnWidthFraction(col);
  const style = { minWidth: 0, boxSizing: 'border-box' };
  let hasLayout = false;

  if (meta.totalBetweenGapPx > 0) {
    const basis = columnFlexBasisWithGaps(col, meta.totalBetweenGapPx);
    style.flex = `0 0 ${basis}`;
    style.maxWidth = basis;
    style.width = basis;
    if (!meta.useColumnGap && colIdx < columns.length - 1 && meta.gaps[colIdx] > 0) {
      style.marginRight = meta.gaps[colIdx];
    }
    hasLayout = true;
  } else if (isNested) {
    const wPct = `${Number((frac * 100).toFixed(6))}%`;
    style.flex = `0 0 ${wPct}`;
    style.maxWidth = wPct;
    style.width = wPct;
    hasLayout = true;
  } else if (col.width === 'dynamic') {
    const pct = col.customWidth ?? 33.33;
    style.flex = `0 0 ${pct}%`;
    style.maxWidth = `${pct}%`;
    style.width = `${pct}%`;
    hasLayout = true;
  }

  return hasLayout ? style : null;
}

/**
 * Regras CSS por linha (master ou grid interno) com !important para vencer madri-col-md-*.
 */
export function pushRowGridGapStyleRules(layoutStyleRules, row, options = {}) {
  const rowCols = Array.isArray(row?.columns) ? row.columns : [];
  const rowId = row?.id;
  if (!rowId || rowCols.length === 0) return;

  const meta = getRowColumnGapMeta(rowCols);
  if (!options.always && meta.totalBetweenGapPx <= 0) return;

  const totalGap = meta.totalBetweenGapPx;
  const rowGapCss = meta.useColumnGap ? `column-gap: ${meta.columnGapPx}px !important;` : '';

  const desktopColRules = rowCols.map((col, idx) => {
    const frac = columnWidthFraction(col);
    const w = totalGap > 0
      ? `calc((100% - ${totalGap}px) * ${Number(frac.toFixed(6))})`
      : `${Number((frac * 100).toFixed(6))}%`;
    const marginRule = (!meta.useColumnGap && idx < rowCols.length - 1 && meta.gaps[idx] > 0)
      ? `margin-right: ${meta.gaps[idx]}px !important;`
      : 'margin-right: 0 !important;';
    return `  .madri-moodle-layout .madri-row-${rowId} > .madri-col-${col.id},
  .madri-moodle-layout .madri-row-${rowId} > .madri-row-inner > .madri-col-${col.id} {
    flex: 0 0 ${w} !important;
    max-width: ${w} !important;
    width: ${w} !important;
    min-width: 0 !important;
    padding-left: ${COLUMN_GRID_BASE_PADDING_PX}px !important;
    padding-right: ${COLUMN_GRID_BASE_PADDING_PX}px !important;
    ${marginRule}
    margin-bottom: 0 !important;
    box-sizing: border-box !important;
  }`;
  }).join('\n');

  const mobileColRules = rowCols.map((col) =>
    `  .madri-moodle-layout .madri-row-${rowId} > .madri-col-${col.id},
  .madri-moodle-layout .madri-row-${rowId} > .madri-row-inner > .madri-col-${col.id} {
    flex: 0 0 100% !important;
    max-width: 100% !important;
    width: 100% !important;
    padding-left: 8px !important;
    padding-right: 8px !important;
    margin-right: 0 !important;
  }`
  ).join('\n');

  layoutStyleRules.push(`@media (min-width: 768px) {
  .madri-moodle-layout .madri-row-${rowId} {
    display: flex !important;
    flex-wrap: nowrap !important;
    margin-left: -15px !important;
    margin-right: -15px !important;
    ${rowGapCss}
  }
  .madri-moodle-layout .madri-row-${rowId} > .madri-row-inner {
    display: flex !important;
    flex-wrap: nowrap !important;
    ${rowGapCss}
  }
${desktopColRules}
}`);
  layoutStyleRules.push(`@media (max-width: 767px) {
  .madri-moodle-layout .madri-row-${rowId} {
    flex-wrap: wrap !important;
    margin-left: -8px !important;
    margin-right: -8px !important;
    row-gap: 12px !important;
    column-gap: 0 !important;
  }
  .madri-moodle-layout .madri-row-${rowId} > .madri-row-inner {
    flex-wrap: wrap !important;
    row-gap: 12px !important;
    column-gap: 0 !important;
  }
${mobileColRules}
}`);
}

/** @deprecated alias */
export function pushNestedGridLayoutStyleRules(layoutStyleRules, row, _rowGapTotalPx = 0) {
  pushRowGridGapStyleRules(layoutStyleRules, row, { always: true });
}
